import klavioCountryDialCodes from './KlavioCountryDialCodes';

const FORM_INITIALIZED_ATTRIBUTE = 'data-klavio-signup-initialized';
const POPUP_INITIALIZED_ATTRIBUTE = 'data-klavio-signup-popup-initialized';
const COUNTRY_FALLBACK_CODE = 'SE';
const SIGNUP_COMPLETED_COOKIE = 'boob_klavio_signup_completed_v1';
const SIGNUP_COMPLETED_DAYS = 365;
const POPUP_DISABLE_QUERY_KEY = 'monthlyNoKlaviyoPopup';

const COUNTRY_DATA = (klavioCountryDialCodes || [])
    .filter((item) => item && item.code && item.name && item.dial)
    .map((item) => ({
        code: String(item.code).toUpperCase(),
        name: String(item.name),
        dial: String(item.dial),
    }))
    .sort((a, b) =>
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
    );

const COUNTRY_BY_CODE = COUNTRY_DATA.reduce((accumulator, item) => {
    if (!accumulator[item.code]) {
        accumulator[item.code] = item;
    }
    return accumulator;
}, {});

const COUNTRY_BY_DIAL = COUNTRY_DATA.reduce((accumulator, item) => {
    if (!accumulator[item.dial]) {
        accumulator[item.dial] = item;
    }
    return accumulator;
}, {});

let recaptchaScriptPromise = null;

const parsePositiveInt = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const parseNonNegativeInt = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const getCookie = (name) => {
    if (!name) {
        return null;
    }

    const prefix = `${name}=`;
    const cookies = (document.cookie || '').split(';');
    for (let i = 0; i < cookies.length; i += 1) {
        const current = cookies[i].trim();
        if (current.indexOf(prefix) === 0) {
            return decodeURIComponent(current.substring(prefix.length));
        }
    }

    return null;
};

const setCookie = (name, value, days) => {
    if (!name) {
        return;
    }

    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    document.cookie = `${name}=${encodeURIComponent(
        value
    )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

const hasDisablePopupQueryFlag = () => {
    if (typeof window === 'undefined' || !window.location) {
        return false;
    }

    const search = window.location.search || '';
    if (!search) {
        return false;
    }

    try {
        const queryParams = new URLSearchParams(search);
        for (const [key] of queryParams.entries()) {
            if (
                String(key).toLowerCase() ===
                POPUP_DISABLE_QUERY_KEY.toLowerCase()
            ) {
                return true;
            }
        }
    } catch (error) {
        return false;
    }

    return false;
};

const isSignupCompleted = () => getCookie(SIGNUP_COMPLETED_COOKIE) === '1';

const markSignupCompleted = () => {
    setCookie(SIGNUP_COMPLETED_COOKIE, '1', SIGNUP_COMPLETED_DAYS);
};

const hideElement = (element) => {
    if (!element) {
        return;
    }

    element.hidden = true;
    element.setAttribute('aria-hidden', 'true');
    element.style.display = 'none';
};

const getSelectedCountryCode = () => {
    const litium = window.__litium || {};
    const constants = litium.constants || {};
    const countries = constants.countries || [];

    for (let i = 0; i < countries.length; i += 1) {
        if (countries[i] && countries[i].selected && countries[i].value) {
            return String(countries[i].value).toUpperCase();
        }
    }

    const lang = (document.documentElement && document.documentElement.lang
        ? document.documentElement.lang
        : ''
    ).toLowerCase();
    if (lang.indexOf('sv') === 0) {
        return 'SE';
    }
    if (
        lang.indexOf('nb') === 0 ||
        lang.indexOf('no') === 0 ||
        lang.indexOf('nn') === 0
    ) {
        return 'NO';
    }
    if (lang.indexOf('da') === 0) {
        return 'DK';
    }
    if (lang.indexOf('de') === 0) {
        return 'DE';
    }

    return COUNTRY_FALLBACK_CODE;
};

const loadRecaptchaScript = () => {
    if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
        return Promise.resolve(window.grecaptcha);
    }

    if (recaptchaScriptPromise) {
        return recaptchaScriptPromise;
    }

    recaptchaScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (
                window.grecaptcha &&
                typeof window.grecaptcha.render === 'function'
            ) {
                resolve(window.grecaptcha);
                return;
            }

            reject(
                new Error('Google reCAPTCHA was loaded but not initialized.')
            );
        };
        script.onerror = () =>
            reject(new Error('Failed to load Google reCAPTCHA script.'));
        document.head.appendChild(script);
    });

    return recaptchaScriptPromise;
};

const normalizeMessage = (message, fallback) => {
    const value = typeof message === 'string' ? message.trim() : '';
    return value || fallback;
};

const normalizePhoneDigits = (value) =>
    String(value || '').replace(/[^0-9]/g, '').trim();

const setFormMessage = (form, message, state) => {
    const messageElement = form.querySelector('.js-klavio-signup-message');
    if (!messageElement) {
        return;
    }

    messageElement.classList.remove('is-success', 'is-error');
    messageElement.textContent = message || '';

    if (state === 'success') {
        messageElement.classList.add('is-success');
    } else if (state === 'error') {
        messageElement.classList.add('is-error');
    }
};

const setFormLoading = (form, isLoading, loadingMessage) => {
    if (!form) {
        return;
    }

    const loadingElement = form.querySelector('.js-klavio-signup-loading');
    const loadingTextElement = form.querySelector(
        '.js-klavio-signup-loading-text'
    );

    if (loadingTextElement && loadingMessage) {
        loadingTextElement.textContent = loadingMessage;
    }

    form.classList.toggle('is-loading', isLoading);
    if (loadingElement) {
        loadingElement.hidden = !isLoading;
        loadingElement.setAttribute(
            'aria-hidden',
            isLoading ? 'false' : 'true'
        );
    }
};

const setFormSuccessState = (form, isSuccess) => {
    if (!form) {
        return;
    }

    form.classList.toggle('is-success-state', isSuccess);
};

const closePopup = (popup) => {
    if (!popup) {
        return;
    }

    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
};

const hideSignupUi = () => {
    const blocks = document.querySelectorAll('.klavio-signup-block');
    blocks.forEach((block) => hideElement(block));

    const popups = document.querySelectorAll('.js-klavio-signup-popup');
    popups.forEach((popup) => hideElement(popup));
};

const initializePopup = (popupElement) => {
    if (
        !popupElement ||
        popupElement.getAttribute(POPUP_INITIALIZED_ATTRIBUTE) === 'true'
    ) {
        return;
    }

    popupElement.setAttribute(POPUP_INITIALIZED_ATTRIBUTE, 'true');

    const cookieName =
        popupElement.dataset.cookieName || 'boob_klavio_signup_seen_v1';
    const cookieDays = parsePositiveInt(popupElement.dataset.cookieDays, 30);
    const delayMs = parseNonNegativeInt(popupElement.dataset.delayMs, 0);

    if (hasDisablePopupQueryFlag()) {
        closePopup(popupElement);
        return;
    }

    if (isSignupCompleted() || getCookie(cookieName)) {
        closePopup(popupElement);
        return;
    }

    const openPopup = () => {
        popupElement.hidden = false;
        popupElement.setAttribute('aria-hidden', 'false');
        setCookie(cookieName, '1', cookieDays);
    };

    if (delayMs <= 0) {
        openPopup();
    } else {
        window.setTimeout(openPopup, delayMs);
    }

    popupElement.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        if (
            target.closest('.js-klavio-signup-popup-close') ||
            target === popupElement
        ) {
            closePopup(popupElement);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !popupElement.hidden) {
            closePopup(popupElement);
        }
    });
};

const initializeCountryPicker = (formElement) => {
    const picker = formElement.querySelector('.js-klavio-signup-country');
    const prefixInput = formElement.querySelector(
        '.js-klavio-signup-prefix-value'
    );
    const countryInput = formElement.querySelector(
        '.js-klavio-signup-country-value'
    );

    if (!picker || !prefixInput) {
        return {
            selectPreferredCountry: () => {},
        };
    }

    const toggle = picker.querySelector('.js-klavio-signup-country-toggle');
    const menu = picker.querySelector('.js-klavio-signup-country-menu');
    const list = picker.querySelector('.js-klavio-signup-country-list');
    const label = picker.querySelector('.js-klavio-signup-country-label');
    const flag = picker.querySelector('.js-klavio-signup-country-flag');
    const defaultCountryCode = String(
        picker.dataset.defaultCountry || ''
    ).toUpperCase();

    let selected = null;

    const closeMenu = () => {
        if (!menu) {
            return;
        }

        picker.classList.remove('is-open');
        menu.hidden = true;
        menu.classList.remove('is-open');
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
        }
    };

    const openMenu = () => {
        if (!menu) {
            return;
        }

        picker.classList.add('is-open');
        menu.hidden = false;
        menu.classList.add('is-open');
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'true');
        }
    };

    const updateSelectedButtonState = () => {
        if (!list || !selected) {
            return;
        }

        const buttons = list.querySelectorAll(
            '.js-klavio-signup-country-option'
        );
        buttons.forEach((button) => {
            const isSelected = button.dataset.countryCode === selected.code;
            button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            button.classList.toggle('is-selected', isSelected);
        });
    };

    const setSelectedCountry = (country) => {
        if (!country) {
            return;
        }

        selected = country;
        prefixInput.value = country.dial;

        if (countryInput) {
            countryInput.value = country.code;
        }

        if (label) {
            label.textContent = `${country.name} (${country.dial})`;
        }

        if (flag) {
            flag.className = `lang__code klavio-signup-country__flag js-klavio-signup-country-flag flag:${country.code}`;
        }

        updateSelectedButtonState();
    };

    const buildList = () => {
        if (!list) {
            return;
        }

        const fragment = document.createDocumentFragment();

        COUNTRY_DATA.forEach((country) => {
            const item = document.createElement('li');
            item.className = 'klavio-signup-country__item';

            const button = document.createElement('button');
            button.type = 'button';
            button.className =
                'klavio-signup-country__option js-klavio-signup-country-option';
            button.dataset.countryCode = country.code;
            button.dataset.countryDial = country.dial;
            button.setAttribute('role', 'option');

            const flagElement = document.createElement('span');
            flagElement.className = `lang__code klavio-signup-country__item-flag flag:${country.code}`;

            const nameElement = document.createElement('span');
            nameElement.className = 'klavio-signup-country__item-name';
            nameElement.textContent = country.name;

            const dialElement = document.createElement('span');
            dialElement.className = 'klavio-signup-country__item-dial';
            dialElement.textContent = country.dial;

            button.appendChild(flagElement);
            button.appendChild(nameElement);
            button.appendChild(dialElement);
            button.addEventListener('click', () => {
                setSelectedCountry(country);
                closeMenu();
            });

            item.appendChild(button);
            fragment.appendChild(item);
        });

        list.innerHTML = '';
        list.appendChild(fragment);
    };

    const resolvePreferredCountry = (explicitCountryCode) => {
        const codeFromInput = String(
            countryInput ? countryInput.value : ''
        ).toUpperCase();
        if (codeFromInput && COUNTRY_BY_CODE[codeFromInput]) {
            return COUNTRY_BY_CODE[codeFromInput];
        }

        const codeFromArgument = String(
            explicitCountryCode || ''
        ).toUpperCase();
        if (codeFromArgument && COUNTRY_BY_CODE[codeFromArgument]) {
            return COUNTRY_BY_CODE[codeFromArgument];
        }

        const codeFromDefault = String(defaultCountryCode || '').toUpperCase();
        if (codeFromDefault && COUNTRY_BY_CODE[codeFromDefault]) {
            return COUNTRY_BY_CODE[codeFromDefault];
        }

        const prefixFromInput = String(prefixInput.value || '').trim();
        if (prefixFromInput && COUNTRY_BY_DIAL[prefixFromInput]) {
            return COUNTRY_BY_DIAL[prefixFromInput];
        }

        const selectedCountryCode = getSelectedCountryCode();
        if (selectedCountryCode && COUNTRY_BY_CODE[selectedCountryCode]) {
            return COUNTRY_BY_CODE[selectedCountryCode];
        }

        return (
            COUNTRY_BY_CODE[COUNTRY_FALLBACK_CODE] || COUNTRY_DATA[0] || null
        );
    };

    const selectPreferredCountry = (explicitCountryCode) => {
        const country = resolvePreferredCountry(explicitCountryCode);
        if (country) {
            setSelectedCountry(country);
        }
    };

    buildList();
    selectPreferredCountry();

    if (toggle && menu) {
        const toggleMenu = (event) => {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (menu.hidden) {
                openMenu();
            } else {
                closeMenu();
            }
        };

        if (window.PointerEvent) {
            toggle.addEventListener('pointerdown', (event) =>
                toggleMenu(event)
            );
        } else {
            toggle.addEventListener('click', (event) => toggleMenu(event));
        }

        toggle.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                closeMenu();
            }
        });
    }

    if (menu) {
        menu.addEventListener('click', (event) => {
            event.stopPropagation();
        });
    }

    document.addEventListener('pointerdown', (event) => {
        if (!(event.target instanceof Node) || !picker.contains(event.target)) {
            closeMenu();
        }
    });

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Node) || !picker.contains(event.target)) {
            closeMenu();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
    });

    return {
        selectPreferredCountry,
    };
};

const initializeForm = (formElement) => {
    if (
        !formElement ||
        formElement.getAttribute(FORM_INITIALIZED_ATTRIBUTE) === 'true'
    ) {
        return;
    }

    formElement.setAttribute(FORM_INITIALIZED_ATTRIBUTE, 'true');
    setFormSuccessState(formElement, false);

    const endpoint = formElement.dataset.endpoint || '/api/klavio';
    const successMessage = normalizeMessage(
        formElement.dataset.successMessage,
        'Thanks! You are now signed up.'
    );
    const errorMessage = normalizeMessage(
        formElement.dataset.errorMessage,
        'Something went wrong, please try again.'
    );
    const loadingMessage = normalizeMessage(
        formElement.dataset.loadingMessage,
        'KlavioSignup.LoadingText'
    );
    const recaptchaEnabled =
        String(formElement.dataset.recaptchaEnabled || '').toLowerCase() ===
        'true';
    const recaptchaSiteKey = (
        formElement.dataset.recaptchaSiteKey || ''
    ).trim();
    const emailField = formElement.querySelector('.js-klavio-signup-email');
    const phoneField = formElement.querySelector('input[name="phoneNumber"]');
    const captchaContainer = formElement.querySelector(
        '.js-klavio-signup-captcha'
    );
    const successTitleElement = formElement.querySelector(
        '.js-klavio-signup-success-title'
    );

    initializeCountryPicker(formElement);
    const submitButton = formElement.querySelector('.klavio-signup-form__submit');

    let recaptchaWidgetId = null;
    let recaptchaReady = false;

    if (successTitleElement && !String(successTitleElement.textContent || '').trim()) {
        successTitleElement.textContent = successMessage;
    }

    const hasValidEmail = () => {
        const emailValue = emailField ? String(emailField.value || '').trim() : '';
        return emailValue.indexOf('@') > 0;
    };

    const hasValidPhone = () => {
        const phoneValue = phoneField ? String(phoneField.value || '').trim() : '';
        const digits = normalizePhoneDigits(phoneValue);
        return phoneValue.length > 0 && digits.length > 3;
    };

    const updateSubmitEnabledState = () => {
        if (!submitButton) {
            return;
        }

        submitButton.disabled = !(hasValidEmail() && hasValidPhone());
    };

    const ensureCaptchaVisibleState = async () => {
        if (!recaptchaEnabled || !captchaContainer || !emailField) {
            return;
        }

        const shouldShowCaptcha = emailField.value.indexOf('@') >= 0;
        captchaContainer.hidden = !shouldShowCaptcha;

        if (!shouldShowCaptcha) {
            if (recaptchaReady && window.grecaptcha) {
                try {
                    window.grecaptcha.reset(recaptchaWidgetId);
                } catch (error) {
                    // Ignore widget reset failures.
                }
            }

            return;
        }

        if (recaptchaReady || !recaptchaSiteKey) {
            return;
        }

        try {
            const grecaptcha = await loadRecaptchaScript();
            recaptchaWidgetId = grecaptcha.render(captchaContainer, {
                sitekey: recaptchaSiteKey,
            });
            recaptchaReady = true;
        } catch (error) {
            setFormMessage(formElement, errorMessage, 'error');
        }
    };

    if (emailField) {
        emailField.addEventListener('input', () => {
            ensureCaptchaVisibleState();
            updateSubmitEnabledState();
        });
    }

    if (phoneField) {
        phoneField.addEventListener('input', () => {
            updateSubmitEnabledState();
        });
    }

    formElement.addEventListener('submit', async (event) => {
        event.preventDefault();
        setFormMessage(formElement, '', null);
        setFormSuccessState(formElement, false);

        const formData = new FormData(formElement);
        const email = (formData.get('email') || '').toString().trim();
        const phonePrefix = (formData.get('phonePrefix') || '')
            .toString()
            .trim();
        const phoneNumber = (formData.get('phoneNumber') || '')
            .toString()
            .trim();
        const pageProof = (formElement.dataset.pageProof || '')
            .toString()
            .trim();
        const formCulture = (formElement.dataset.culture || '')
            .toString()
            .trim();

        if (!email || email.indexOf('@') < 0) {
            setFormMessage(formElement, errorMessage, 'error');
            return;
        }

        if (!phoneNumber || normalizePhoneDigits(phoneNumber).length < 4) {
            setFormMessage(formElement, errorMessage, 'error');
            return;
        }

        await ensureCaptchaVisibleState();

        let googleToken = null;
        if (recaptchaEnabled) {
            if (!recaptchaReady || !window.grecaptcha) {
                setFormMessage(formElement, errorMessage, 'error');
                return;
            }

            googleToken = window.grecaptcha.getResponse(recaptchaWidgetId);
            if (!googleToken) {
                setFormMessage(formElement, errorMessage, 'error');
                return;
            }
        }

        if (submitButton) {
            submitButton.disabled = true;
        }
        setFormLoading(formElement, true, loadingMessage);

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    phonePrefix,
                    phoneNumber,
                    googleToken,
                    pageProof,
                    sourceUrl: window.location.href,
                    culture:
                        formCulture ||
                        (document.documentElement &&
                        document.documentElement.lang
                            ? document.documentElement.lang
                            : ''),
                }),
            });

            const payload = await response.json().catch(() => ({}));
            const alreadySubscribed = payload && payload.alreadySubscribed === true;
            if ((!response.ok || payload.success !== true) && !alreadySubscribed) {
                const serverMessage = normalizeMessage(payload.message, errorMessage);
                const detailMessage = normalizeMessage(payload.detail, '');
                const combinedMessage = detailMessage
                    ? `${serverMessage} (${detailMessage})`
                    : serverMessage;
                setFormMessage(formElement, combinedMessage, 'error');
                return;
            }

            if (successTitleElement) {
                successTitleElement.textContent = successMessage;
            }
            setFormMessage(formElement, '', null);
            setFormSuccessState(formElement, true);
            markSignupCompleted();

            if (recaptchaReady && window.grecaptcha) {
                try {
                    window.grecaptcha.reset(recaptchaWidgetId);
                } catch (error) {
                    // Ignore widget reset failures.
                }
            }

        } catch (error) {
            setFormMessage(formElement, errorMessage, 'error');
        } finally {
            setFormLoading(formElement, false, loadingMessage);
            if (submitButton) {
                if (!formElement.classList.contains('is-success-state')) {
                    updateSubmitEnabledState();
                } else {
                    submitButton.disabled = true;
                }
            }
        }
    });

    ensureCaptchaVisibleState();
    updateSubmitEnabledState();
};

const initializeKlaviyoSignForm = () => {
    if (isSignupCompleted()) {
        hideSignupUi();
        return;
    }

    const forms = document.querySelectorAll('.js-klavio-signup-form');
    forms.forEach((form) => initializeForm(form));

    const popups = document.querySelectorAll('.js-klavio-signup-popup');
    popups.forEach((popup) => initializePopup(popup));
};

export default initializeKlaviyoSignForm;

